/* ==========================================================================
   global.js — The Jinnah Project Paris
   Cart (AJAX API + section rendering), drawers/dialogs, a11y utilities,
   toast queue. Budget ≤ 25 KB (SPEC §17.1, §18.1).
   Every feature here ENHANCES a server-rendered fallback (SPEC §18.3):
   real forms, real links, native details/summary keep working without JS.
   ========================================================================== */

/* Idempotent custom-element registration. This module can be evaluated more
   than once per document (the layout loads global.js?v=… directly while
   product.js imports the bare ./global.js specifier — different module keys),
   and a duplicate customElements.define() throws and aborts the importer.
   Guarding every definition makes re-evaluation a no-op instead of a crash. */
function defineElement(name, ctor, options) {
  if (!customElements.get(name)) customElements.define(name, ctor, options);
}

/* --------------------------------------------------------------------------
   A11y utilities (SPEC §20)
   -------------------------------------------------------------------------- */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function trapFocus(container, { initial } = {}) {
  const nodes = () => [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
  const first = initial || nodes()[0];
  const previouslyFocused = document.activeElement;
  first?.focus();

  function onKeydown(e) {
    if (e.key !== 'Tab') return;
    const list = nodes();
    if (list.length === 0) return;
    const firstEl = list[0];
    const lastEl = list[list.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  }

  container.addEventListener('keydown', onKeydown);
  return () => {
    container.removeEventListener('keydown', onKeydown);
    previouslyFocused?.focus?.();
  };
}

/* Screen-reader announcements (SPEC §20: cart updates, ATC success) */
let liveRegion;
export function announce(message) {
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.className = 'visually-hidden';
    document.body.append(liveRegion);
  }
  liveRegion.textContent = '';
  requestAnimationFrame(() => (liveRegion.textContent = message));
}

export const reducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* --------------------------------------------------------------------------
   Toast — queue rule: max one visible, successors replace with 100 ms
   crossfade (SPEC §28). Pausable on hover/focus (SPEC §7.4).
   -------------------------------------------------------------------------- */

const toastEl = () => document.getElementById('toast');
let toastTimer;

export function toast(message, { duration = 4000 } = {}) {
  const el = toastEl();
  if (!el) return;
  clearTimeout(toastTimer);

  const show = () => {
    el.textContent = message;
    el.classList.add('is-visible');
    let remaining = duration;
    let started = Date.now();

    const pause = () => {
      clearTimeout(toastTimer);
      remaining -= Date.now() - started;
    };
    const resume = () => {
      started = Date.now();
      toastTimer = setTimeout(hide, Math.max(remaining, 500));
    };
    const hide = () => {
      el.classList.remove('is-visible');
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
    };

    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);
    resume();
  };

  if (el.classList.contains('is-visible')) {
    el.classList.remove('is-visible');
    setTimeout(show, 100); // crossfade replacement
  } else {
    show();
  }
}

/* --------------------------------------------------------------------------
   Cart store — Shopify AJAX Cart API with section rendering (SPEC §17.3).
   Optimistic quantity updates with rollback-on-error (SPEC §23.3).
   -------------------------------------------------------------------------- */

const CART_SECTIONS = ['cart-drawer'];

async function cartRequest(path, payload) {
  let response;
  try {
    response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...payload,
        sections: CART_SECTIONS.join(','),
        sections_url: window.location.pathname,
      }),
    });
  } catch (networkErr) {
    // Offline / flaky network (SPEC §23.2): retriable toast, no silent loss
    toast(window.tjpStrings?.offline || 'Connexion perdue — réessayer');
    throw networkErr;
  }
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.description || data.message || 'cart_error');
    err.status = response.status;
    throw err;
  }
  return data;
}

export const cart = {
  async add(items) {
    const data = await cartRequest('cart/add.js', { items });
    data.__added = true; // lets the drawer auto-open / toast on ATC
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  },
  async change(key, quantity) {
    const data = await cartRequest('cart/change.js', { id: key, quantity });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  },
};

/* Delegated drawer-open triggers — survive section re-renders */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-drawer-open]');
  if (!btn) return;
  const drawer = document.getElementById(btn.dataset.drawerOpen);
  if (drawer?.open) {
    e.preventDefault();
    drawer.open();
  }
});

/* Re-render registered cart sections from a section-rendering payload,
   preserving drawer open state across the DOM swap */
document.addEventListener('cart:updated', (e) => {
  const sections = e.detail?.sections;
  if (!sections) return;
  for (const [id, html] of Object.entries(sections)) {
    const target = document.getElementById(`shopify-section-${id}`);
    if (!target || !html) continue;
    const wasOpen = target.querySelector('tjp-drawer[open]');
    const incoming = new DOMParser().parseFromString(html, 'text/html');
    const replacement = incoming.getElementById(`shopify-section-${id}`);
    if (!replacement) continue;
    target.innerHTML = replacement.innerHTML;
    if (wasOpen) {
      requestAnimationFrame(() => target.querySelector('tjp-drawer')?.open());
    }
  }
});

/* --------------------------------------------------------------------------
   <tjp-drawer> — slide-over base (cart drawer, mobile menu, filter sheet).
   Focus-trapped, ESC-closes, overlay click closes, focus returns to invoker
   (SPEC §7.4, §20). Server-rendered content; this element only shows/hides.
   -------------------------------------------------------------------------- */

defineElement(
  'tjp-drawer',
  class extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      this.addEventListener('click', (e) => {
        if (e.target.matches('[data-drawer-overlay], [data-drawer-close], [data-drawer-close] *')) {
          e.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open() {
      this.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      const closeBtn = this.querySelector('[data-drawer-close]');
      this.releaseFocus = trapFocus(this, { initial: closeBtn });
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.body.style.overflow = '';
      this.releaseFocus?.();
      this.releaseFocus = null;
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
);

/* --------------------------------------------------------------------------
   <tjp-modal> — dialog pattern for size guide, quick view, lightbox
   (SPEC §7.4). Uses native <dialog> when available.
   -------------------------------------------------------------------------- */

defineElement(
  'tjp-modal',
  class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      document.querySelectorAll(`[data-modal-open="${this.id}"]`).forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.dialog.showModal();
        })
      );
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close(); // overlay click
      });
      this.querySelectorAll('[data-modal-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.dialog.close())
      );
    }
  }
);

/* --------------------------------------------------------------------------
   <quantity-stepper> — bordered ± group wrapping a real number input
   (SPEC §7.4; form semantics preserved).
   -------------------------------------------------------------------------- */

defineElement(
  'quantity-stepper',
  class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn || !this.input) return;
        e.preventDefault();
        const step = Number(btn.dataset.step);
        const next = Math.max(
          Number(this.input.min || 0),
          Number(this.input.value || 0) + step
        );
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
);

/* --------------------------------------------------------------------------
   Wishlist "Ma Sélection" — localStorage, no app (SPEC §24.3, DECISIONS D-009)
   -------------------------------------------------------------------------- */

const WISHLIST_KEY = 'tjp:selection';

export const wishlist = {
  all() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch {
      return [];
    }
  },
  has(handle) {
    return this.all().includes(handle);
  },
  toggle(handle) {
    const list = this.all();
    const idx = list.indexOf(handle);
    if (idx >= 0) list.splice(idx, 1);
    else list.push(handle);
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { list } }));
    return idx < 0;
  },
};

defineElement(
  'wishlist-toggle',
  class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.handle = this.dataset.handle;
      if (!this.button || !this.handle) return;
      this.sync();
      this.button.addEventListener('click', () => {
        const added = wishlist.toggle(this.handle);
        this.sync();
        announce(
          added ? this.dataset.labelRemove || '' : this.dataset.labelAdd || ''
        );
      });
      document.addEventListener('wishlist:updated', () => this.sync());
    }

    sync() {
      const active = wishlist.has(this.handle);
      this.button.setAttribute('aria-pressed', String(active));
    }
  }
);

/* Header wishlist/cart count bubbles */
document.addEventListener('wishlist:updated', (e) => {
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = e.detail.list.length || '';
    el.toggleAttribute('hidden', e.detail.list.length === 0);
  });
});

/* --------------------------------------------------------------------------
   Quick-add (SPEC §10.3): the size sheet is a real /cart/add form (no-JS
   fallback posts and redirects); JS upgrades it to AJAX. Delegated so it
   works inside re-rendered grids.
   -------------------------------------------------------------------------- */

document.addEventListener('submit', async (e) => {
  const form = e.target.closest('[data-quick-add-form]');
  if (!form) return;
  const submitter = e.submitter;
  if (!submitter?.value) return; // size not chosen — let native validation run
  e.preventDefault();
  submitter.setAttribute('aria-busy', 'true');
  try {
    await cart.add([{ id: Number(submitter.value), quantity: 1 }]);
    form.closest('details')?.removeAttribute('open');
  } catch (err) {
    form.submit(); // degrade to the server-rendered flow
  } finally {
    submitter.removeAttribute('aria-busy');
  }
});

/* --------------------------------------------------------------------------
   <scroll-strip> — arrows for scroll-snap strips (SPEC v3 §2.7).
   Wraps a native overflow-x strip; arrows scroll by one item. Hidden until
   JS runs and only shown when content actually overflows.
   -------------------------------------------------------------------------- */

defineElement(
  'scroll-strip',
  class extends HTMLElement {
    connectedCallback() {
      this.strip = this.querySelector(this.dataset.stripTarget || '[data-strip]');
      this.arrows = this.querySelector('[data-strip-arrows]');
      if (!this.strip || !this.arrows) return;
      const sync = () => {
        this.arrows.hidden = this.strip.scrollWidth <= this.strip.clientWidth + 8;
      };
      sync();
      new ResizeObserver(sync).observe(this.strip);
      const step = () => {
        const item = this.strip.querySelector(':scope > *');
        return item ? item.getBoundingClientRect().width + 24 : this.strip.clientWidth * 0.8;
      };
      this.querySelector('[data-strip-prev]')?.addEventListener('click', () =>
        this.strip.scrollBy({ left: -step(), behavior: 'smooth' })
      );
      this.querySelector('[data-strip-next]')?.addEventListener('click', () =>
        this.strip.scrollBy({ left: step(), behavior: 'smooth' })
      );
    }
  }
);

/* --------------------------------------------------------------------------
   Error boundary for third-party blocks (SPEC §23.2): a failing embedded
   script must collapse its block silently, never render a broken frame.
   Sections opt in with data-error-boundary.
   -------------------------------------------------------------------------- */

window.addEventListener(
  'error',
  (e) => {
    const boundary = e.target?.closest?.('[data-error-boundary]');
    if (boundary) boundary.hidden = true;
  },
  true
);
